import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useJsonLd, getSiteUrl } from '@/hooks/useJsonLd';
import { featuredArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation(['common', 'article']);

  const currentArticle = featuredArticles.find((a) => a.slug === slug);
  const otherArticles = featuredArticles.filter((a) => a.slug !== slug).slice(0, 3);

  const siteUrl = getSiteUrl();

  // Inject Article JSON-LD
  useJsonLd(
    currentArticle
      ? {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: currentArticle.title,
          description: currentArticle.excerpt,
          url: `${siteUrl}/article/${currentArticle.slug}`,
          datePublished: '2026-01-15T08:00:00+01:00',
          dateModified: '2026-06-20T10:00:00+01:00',
          author: {
            '@type': 'Organization',
            name: 'Hipobuy Spreadsheet',
            url: siteUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Hipobuy Spreadsheet',
            url: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/favicon.ico`,
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/article/${currentArticle.slug}`,
          },
          inLanguage: 'fr',
          isAccessibleForFree: true,
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Article | Hipobuy Spreadsheet',
          url: siteUrl,
          inLanguage: 'fr',
        },
  );

  // Inject FAQPage JSON-LD
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: t('article_faq_q1'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('article_faq_a1'),
        },
      },
      {
        '@type': 'Question',
        name: t('article_faq_q2'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('article_faq_a2'),
        },
      },
      {
        '@type': 'Question',
        name: t('article_faq_q3'),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t('article_faq_a3'),
        },
      },
    ],
  });

  useEffect(() => {
    // Scroll to top when article loads
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (currentArticle) {
      document.title = `${currentArticle.title} | Hipobuy Spreadsheet`;
    } else {
      document.title = 'Article | Hipobuy Spreadsheet';
    }
  }, [currentArticle]);

  if (!currentArticle) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">Article non trouvé</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors font-medium">
              {t('back_home')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        {/* Article Hero */}
        <article className="pt-24 md:pt-32 pb-10 md:pb-16 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <Link to="/" className="text-xs text-foreground-400 hover:text-primary-600 transition-colors">
                {t('nav_home')}
              </Link>
              <span className="text-xs text-foreground-300 mx-2">/</span>
              <span className="text-xs text-foreground-500">{currentArticle.title}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
              {currentArticle.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mt-4 leading-relaxed">
              {t('article_hero_subtitle')}
            </p>
          </div>
        </article>

        {/* Article Content */}
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-16">
          {/* Section 1: Introduction */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
              {t('article_section_1_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              {t('article_section_1_content_1')}
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {t('article_section_1_content_2')}
            </p>
          </section>

          {/* Section 2: Benefits */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
              {t('article_section_2_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              {t('article_section_2_intro')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-primary-50 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-rocket-line text-primary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">Organisation Intelligente</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Structurez vos données de manière intuitive avec le hipobuy spreadsheet. Catégorisation automatique et vues personnalisables pour une productivité maximale.</p>
              </div>
              <div className="bg-accent-50 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-flashlight-line text-accent-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">Automatisation Puissante</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Automatisez vos tâches répétitives et gagnez des heures chaque semaine. Le hipobuy spreadsheet intègre des workflows configurables.</p>
              </div>
              <div className="bg-secondary-50 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                  <i className="ri-line-chart-line text-secondary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">Analyse Avancée</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Visualisez vos données avec des graphiques dynamiques et des tableaux de bord personnalisés pour prendre les meilleures décisions.</p>
              </div>
              <div className="bg-primary-100/50 rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-shield-check-line text-primary-600"></i>
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">Compatibilité Totale</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Importez et exportez vos données depuis Excel, Google Sheets et d'autres formats. Le hipobuy spreadsheet s'intègre à votre écosystème existant.</p>
              </div>
            </div>
          </section>

          {/* CTA Mid-article */}
          <div className="bg-foreground-900 rounded-2xl p-6 md:p-8 text-center mb-12">
            <p className="text-background-50 font-heading text-lg md:text-xl font-semibold mb-3">
              Prêt à découvrir le hipobuy spreadsheet ?
            </p>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('cta_access')} <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Section 3: Getting Started */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
              {t('article_section_3_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
              {t('article_section_3_intro')}
            </p>
            <ol className="space-y-5">
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-semibold text-sm flex-shrink-0">1</span>
                <div>
                  <strong className="text-foreground-900">Créez votre compte</strong>
                  <p className="text-sm text-foreground-600 mt-1">L&apos;inscription au hipobuy spreadsheet est rapide et gratuite. Accédez à toutes les fonctionnalités essentielles sans engagement.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-semibold text-sm flex-shrink-0">2</span>
                <div>
                  <strong className="text-foreground-900">Choisissez un template</strong>
                  <p className="text-sm text-foreground-600 mt-1">Sélectionnez parmi des dizaines de templates hipobuy spreadsheet prêts à l&apos;emploi, adaptés à votre secteur d&apos;activité.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-semibold text-sm flex-shrink-0">3</span>
                <div>
                  <strong className="text-foreground-900">Importez vos données</strong>
                  <p className="text-sm text-foreground-600 mt-1">Transférez facilement vos fichiers Excel ou Google Sheets dans le hipobuy spreadsheet en quelques clics.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-semibold text-sm flex-shrink-0">4</span>
                <div>
                  <strong className="text-foreground-900">Optimisez et automatisez</strong>
                  <p className="text-sm text-foreground-600 mt-1">Configurez vos automatisations et laissez le hipobuy spreadsheet travailler pour vous. Suivez vos performances en temps réel.</p>
                </div>
              </li>
            </ol>
          </section>

          {/* Section 4: Comparison */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
              {t('article_section_4_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
              {t('article_section_4_intro')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left py-3 px-4 font-semibold text-foreground-800">Fonctionnalité</th>
                    <th className="text-center py-3 px-4 font-semibold text-primary-700">Hipobuy Spreadsheet</th>
                    <th className="text-center py-3 px-4 font-semibold text-foreground-700">Excel</th>
                    <th className="text-center py-3 px-4 font-semibold text-foreground-700">Google Sheets</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-background-200/50">
                    <td className="py-3 px-4 text-foreground-700">Automatisation</td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-foreground-400 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="py-3 px-4 text-foreground-700">Templates prêts à l&apos;emploi</td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-foreground-400 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="py-3 px-4 text-foreground-700">Suivi SEO intégré</td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="py-3 px-4 text-foreground-700">Version gratuite</td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-foreground-400 text-lg"></i></td>
                  </tr>
                  <tr className="border-t border-background-200/50">
                    <td className="py-3 px-4 text-foreground-700">Analyse IA</td>
                    <td className="text-center py-3 px-4"><i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-circle-fill text-foreground-300 text-lg"></i></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5: Use Cases */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
              {t('article_section_5_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
              {t('article_section_5_intro')}
            </p>
            <div className="space-y-4">
              <div className="flex gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Spécialistes SEO</strong>
                  <p className="text-sm text-foreground-600 mt-1">Suivi de positionnement, analyse des backlinks et reporting automatisé avec le hipobuy spreadsheet.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">E-commerçants</strong>
                  <p className="text-sm text-foreground-600 mt-1">Gestion des stocks, suivi des ventes et analyse des performances produit avec le hipobuy spreadsheet.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-briefcase-line text-secondary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Freelances</strong>
                  <p className="text-sm text-foreground-600 mt-1">Facturation, suivi clients et gestion de projets simplifiés grâce au hipobuy spreadsheet.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: FAQ */}
          <section className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
              {t('article_section_6_title')}
            </h2>
            <div className="space-y-4">
              <div className="rounded-xl border border-background-200/70 p-5">
                <h3 className="font-semibold text-foreground-900 mb-2">{t('article_faq_q1')}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t('article_faq_a1')}</p>
              </div>
              <div className="rounded-xl border border-background-200/70 p-5">
                <h3 className="font-semibold text-foreground-900 mb-2">{t('article_faq_q2')}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t('article_faq_a2')}</p>
              </div>
              <div className="rounded-xl border border-background-200/70 p-5">
                <h3 className="font-semibold text-foreground-900 mb-2">{t('article_faq_q3')}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t('article_faq_a3')}</p>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12 bg-background-100 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3">
              {t('article_conclusion_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {t('article_conclusion')}
            </p>
          </section>

          {/* Bottom CTA */}
          <div className="text-center mb-12">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-background-50 text-sm md:text-base font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('cta_access')} <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Related Articles */}
          <section className="border-t border-background-200/70 pt-10">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
              {t('related_articles_title')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {otherArticles.map((article, idx) => (
                <Link
                  key={idx}
                  to={`/article/${article.slug}`}
                  className="group p-4 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <h4 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug mb-2">
                    {article.title}
                  </h4>
                  <span className="text-xs text-primary-600 font-medium whitespace-nowrap">
                    {t('read_more')} <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Back to home */}
          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors font-medium cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> {t('back_home')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}